// SUB-OFFICE 7 — identity. Three copies of one file: a UUID in localStorage,
// mirrored to IndexedDB, echoed by the server as a first-party cookie —
// because iOS Safari misplaces files after seven days and the Bureau does not
// tolerate misplaced files. The FILE TRANSFER CODE (number + claim phrase)
// closes the last gap; POST /api/reclaim re-binds a lost file.
//
// Shape stored under LS_PROFILE (client snapshot, never authoritative):
//   { uuid, ticket_no, designation, standing, grade, claim_phrase,
//     form1_done, intake_done, audio_consent, ceremonies }

const LS_UUID = 'bureau_uuid';
const LS_PROFILE = 'bureau_profile';
const IDB_NAME = 'bureau';
const IDB_STORE = 'file';

export interface Profile {
  uuid: string;
  ticket_no?: number;
  designation?: string;
  standing?: number;
  grade?: string;
  claim_phrase?: string;
  form1_done?: boolean;
  intake_done?: boolean;
  audio_consent?: boolean;
  ceremonies?: number; // lifetime full ceremonies witnessed client-side
}

function mintUuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // older WebKit: assemble from getRandomValues
    const b = new Uint8Array(16);
    crypto.getRandomValues(b);
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }
}

function cookieUuid(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)bureau_file=([A-Za-z0-9\-_]{8,64})/);
  return m ? m[1] : null;
}

// ---------------------------------------------------------------------------
// IndexedDB mirror (best-effort; the Bureau files in triplicate, quietly)
// ---------------------------------------------------------------------------

function idbOpen(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(IDB_STORE)) req.result.createObjectStore(IDB_STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await idbOpen();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbSet(key: string, value: string): Promise<void> {
  const db = await idbOpen();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

// ---------------------------------------------------------------------------

let currentUuid: string | null = null;

/** Resolve the identity: localStorage, IndexedDB mirror, cookie, else mint. */
export async function resolveIdentity(): Promise<string> {
  let uuid: string | null = null;
  try {
    uuid = localStorage.getItem(LS_UUID);
  } catch {
    /* storage sealed */
  }
  if (!uuid) uuid = await idbGet(LS_UUID);
  if (!uuid) uuid = cookieUuid();
  if (!uuid) uuid = mintUuid();
  adoptIdentity(uuid);
  return uuid;
}

/** Adopt (and mirror) an identity — also the reissue/reclaim swap path. */
export function adoptIdentity(uuid: string): void {
  currentUuid = uuid;
  try {
    localStorage.setItem(LS_UUID, uuid);
  } catch {
    /* noted */
  }
  void idbSet(LS_UUID, uuid);
}

export function getUuid(): string {
  return currentUuid || '';
}

// ---------------------------------------------------------------------------
// Profile snapshot (display cache + intake progress; server stays authoritative)
// ---------------------------------------------------------------------------

export function loadProfile(): Profile {
  try {
    const raw = localStorage.getItem(LS_PROFILE);
    if (raw) {
      const p = JSON.parse(raw) as Profile;
      if (p && typeof p.uuid === 'string') return p;
    }
  } catch {
    /* the drawer was empty */
  }
  return { uuid: getUuid() };
}

export function saveProfile(patch: Partial<Profile>): Profile {
  const cur = loadProfile();
  const next: Profile = { ...cur, ...patch, uuid: getUuid() || cur.uuid };
  try {
    localStorage.setItem(LS_PROFILE, JSON.stringify(next));
  } catch {
    /* noted */
  }
  void idbSet(LS_PROFILE, JSON.stringify(next));
  return next;
}

/** Reissue/reclaim: the successor takes the drawer; the predecessor's local
 *  snapshot is replaced wholesale. */
export function replaceProfile(p: Profile): void {
  adoptIdentity(p.uuid);
  try {
    localStorage.setItem(LS_PROFILE, JSON.stringify(p));
  } catch {
    /* noted */
  }
  void idbSet(LS_PROFILE, JSON.stringify(p));
}
